import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { DateInput } from '@eika/date-input';

import useTranslation from '../../../../hooks/useTranslation';
import { EikaModal } from '../../../EikaModal';
import { SelectPanelList } from '../../../SelectPanelList';
import { EndType, endTypes } from '../../../shared/DateField/EndDateType';
import { getEndDate, getEndDateType } from '../../../shared/DateField/utils/getEndDateAndType';

import './EndDateModal.scss';

type EndDateSheetProps = {
    onChange?: (endDate?: ZonedDate) => void;
    minDate?: ZonedDate;
    date?: ZonedDate;
    open?: boolean;
    setOpen: React.Dispatch<boolean>;
};

const EndDateModal: React.FC<EndDateSheetProps> = ({ onChange, date, open = false, setOpen, minDate }) => {
    const translated = useTranslation();
    const [endType, setEndType] = useState<EndType>(getEndDateType(date));
    const [endDate, setEndDate] = useState(date);

    const handleCalendarSelect = (zonedDate?: ZonedDate) => {
        setEndDate(zonedDate);
        setEndType(getEndDateType(zonedDate));
    };

    const handleSelectListClick = (type: EndType) => {
        const targetDate = getEndDate(type);
        setEndDate(type === 'NEVER' ? undefined : getZonedDate(targetDate));
        setEndType(type);
    };

    const saveAndClose = () => {
        onChange?.(endDate);
        setOpen(false);
    };

    useEffect(() => {
        setEndDate(date);
        setEndType(getEndDateType(date));
    }, [date, setEndType, setEndDate]);

    const isNever = endType === 'NEVER';

    return (
        <EikaModal
            id="end-date-modal"
            open={open}
            setOpen={setOpen}
            footerContent={<Button onClick={saveAndClose}>{translated.save}</Button>}
            title={translated.dateField.paymentInterval.lastPaymentDate}
        >
            <div className={classNames('end-date-modal__grid-wrapper')}>
                <SelectPanelList id="payment-interval-selector" className={classNames('end-date-modal__list-wrapper')}>
                    {endTypes.map((end) => (
                        <SelectPanelList.Item<EndType>
                            key={end}
                            value={end}
                            check={endType === end}
                            onClick={handleSelectListClick}
                        >
                            <p className="select-panel-item__end-date">{translated.dateField.endDates[end]}</p>
                        </SelectPanelList.Item>
                    ))}
                </SelectPanelList>

                <section className="end-date-modal__custom-date-selector">
                    {!isNever && (
                        <DateInput
                            onChange={handleCalendarSelect}
                            date={endDate}
                            minDate={minDate ? getZonedDate(minDate) : undefined}
                            label={translated.date}
                            alwaysOpenCalendar
                            alwaysShowYearInCalendar
                        />
                    )}
                </section>
            </div>
        </EikaModal>
    );
};

export default EndDateModal;
